import { DensitySize } from '@ant-design/pro-table/es/components/ToolBar/DensityIcon';
import { isPlatform } from '@ionic/react';
import { Collapse, Descriptions, Divider, Empty, Modal } from 'antd';
import { CustomizeComponent } from 'rc-table/lib/interface';
import {
  cloneElement,
  type JSX,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { ContextApp } from '../contexts/ContextApp';
import { Tools } from '../shared';

import './useProTableForMobile.less';

interface Iprops {
  layout: 'vertical' | 'horizontal';
  expandable?: (record: MobileRecord) => JSX.Element;
  expandableTitle?: string;
}

type MobileRecord = Record<string, unknown> & { id?: string | number };
type MobileColumn = {
  dataIndex?: string;
  index?: number;
  sorter?: unknown;
  className?: string;
  RC_TABLE_INTERNAL_COL_DEFINE?: unknown;
  title?: ReactNode;
  render?: (value: unknown, record: MobileRecord, index: number) => ReactNode;
};
type TablePartProps = {
  columns?: MobileColumn[];
  data?: MobileRecord[];
  expandedKeys?: Set<string | number>;
};
type MobileTableProps = {
  children: ReactElement<TablePartProps>[];
};

const getProperty = (value: unknown, key: string): unknown => {
  if (typeof value !== 'object' || value === null) return undefined;
  return (value as Record<string, unknown>)[key];
};

const isMobileRecord = (value: unknown): value is MobileRecord =>
  typeof value === 'object' && value !== null;

// Ejemplo de uso

// const { mobileOnSizeChangeProTable, changeView, showComponent } =
// useProTableForMobile({
//   layout: 'horizontal',
//   expandable: expandedRowRender,
//   expandableTitle: 'Expandible';
// });

// <ProTable
// onSizeChange={mobileOnSizeChangeProTable}
// components={{
//   table: showComponent(),
// }}
// />

// <Button onClick={changeView} className="buttonChangeProTableView">
//  {Tools.capitalize(t('action.changeView'))}
// </Button>,

//onSizeChange  ===> da funcionalidad de cambiar el tamaño de la tabla en mobile (opcional su uso)
//components ===> cambia el layout de la tabla en mobile para que se vea mejor

/**
 * useProTableForMobile
 *
 * @property layout : 'vertical' | 'horizontal' (por defecto 'horizontal') (opcional)
 * @property expandable : En caso de que se use expandible, enviar funcion que renderiza (opcional)
 * @property expandableTitle : titulo para expandible (opcional)
 *
 * @returns mobileOnSizeChangeProTable, changeView, showComponent
 */
const useProTableForMobile = (props: Iprops) => {
  const { t } = useContext(ContextApp);
  const { layout = 'horizontal', expandable, expandableTitle } = props;

  const [mobileTableSize, setMobileTableSize] = useState<
    'middle' | 'small' | 'default' | undefined
  >();
  const [selectedView, setSelectedView] = useState<'table' | 'card' | null>(
    null,
  );

  const mobileOnSizeChangeProTable = (selectedSize: DensitySize) => {
    switch (selectedSize) {
      case 'small':
        setMobileTableSize('small');
        break;
      case 'middle':
        setMobileTableSize('middle');
        break;
      case 'large':
        setMobileTableSize('default');
        break;
      default:
        setMobileTableSize('default');
    }
  };

  const getTableView = () => {
    if (isPlatform('desktop')) setSelectedView('table');
    else setSelectedView('card');
  };

  useLayoutEffect(() => {
    getTableView();
  }, []);

  const changeView = () => {
    if (selectedView === 'table') setSelectedView('card');
    else setSelectedView('table');
  };

  const MobileComponentsProTable = (protableProps: MobileTableProps) => {
    const headerColumn = protableProps.children[1];
    const sorterColumn = protableProps.children[2];
    const valueColumn = protableProps.children[3];

    const headers = headerColumn.props.columns ?? [];
    const columnsForSorter = sorterColumn.props.columns ?? [];
    const sorterColumnForDisplay = cloneElement(sorterColumn, {
      columns: columnsForSorter.map((column) =>
        column.sorter ? column : { ...column, className: 'hide' },
      ),
    });
    const tableValues = valueColumn.props.data ?? [];
    const expandedKeys: Set<string | number> =
      valueColumn.props.expandedKeys ?? new Set();
    const expandedKeysSignature = [...expandedKeys].join(',');
    const [expandedModalId, setExpandedModalId] = useState<string | number>();

    useEffect(() => {
      const expandedIds = [...expandedKeys];
      setExpandedModalId(expandedIds.at(-1));
    }, [expandedKeysSignature]);

    const headerLabel = (header: MobileColumn) => {
      const titleProps = getProperty(header.title, 'props');
      const labelForItemWithoutSorter = getProperty(titleProps, 'label');
      const titleChildren = getProperty(titleProps, 'children');
      const wrappedTitleChildren = getProperty(titleChildren, 'props');
      const titleChildItems = getProperty(wrappedTitleChildren, 'children');
      const firstTitleChild = Array.isArray(titleChildItems)
        ? titleChildItems[0]
        : undefined;
      const firstTitleChildProps = getProperty(firstTitleChild, 'props');
      const nestedTitle = getProperty(firstTitleChildProps, 'children');
      const nestedTitleProps = getProperty(nestedTitle, 'props');
      const nestedLabel = getProperty(nestedTitleProps, 'label');

      if (header.RC_TABLE_INTERNAL_COL_DEFINE) {
        return expandableTitle;
      }
      if (header.dataIndex === 'option') {
        return Tools.capitalize(t('entity.options'));
      }
      return (labelForItemWithoutSorter ?? nestedLabel) as ReactNode;
    };

    const renderedValues = (header: MobileColumn, value: MobileRecord) => {
      //return Modal para expandible
      const recordId = value.id;
      if (header.RC_TABLE_INTERNAL_COL_DEFINE && recordId !== undefined) {
        if (expandedKeys.has(recordId)) {
          return (
            <Modal
              open={expandedModalId === recordId}
              maskClosable
              className="pro-table-for-mobile__expandable-modal"
              onCancel={() => {
                expandedKeys.delete(recordId);
                setExpandedModalId(undefined);
              }}
              footer={null}
            >
              {expandable?.(value)}
            </Modal>
          );
        }
      }

      //return en caso de no tener render en columnas de la tabla
      const renderedValue = header.render?.(undefined, value, 0);
      if (renderedValue === '-') {
        const key = header.dataIndex;
        return ((key ? value[key] : undefined) || '-') as ReactNode;
      }

      //return en caso de tener render en columnas de la tabla
      return renderedValue;
    };

    const descriptionItems = (value: MobileRecord) => {
      return headers.map((header, headerIndex) => (
        <Descriptions.Item
          label={headerLabel(header)}
          key={value?.id || headerIndex}
          className="pro-table-for-mobile__description-item"
        >
          {renderedValues(header, value) as ReactNode}
        </Descriptions.Item>
      ));
    };

    const sorter = () => {
      const showSorter = columnsForSorter.some((column) => column.sorter);

      return (
        showSorter && (
          <Collapse
            ghost
            className="pro-table-for-mobile__collapse"
            items={[
              {
                key: '1',
                label: Tools.capitalize(t('entity.ordering')),
                className: 'pro-table-for-mobile__sorter-panel',
                style: { display: 'flex', flexDirection: 'column' },
                children: sorterColumnForDisplay,
              },
            ]}
          />
        )
      );
    };

    const thereIsNoColumnSelectedOnSettings =
      headers.length === 1 && !headers[0].index;

    if (thereIsNoColumnSelectedOnSettings || !tableValues.length) {
      let description = t('validation.selectColumns');

      if (!tableValues.length) {
        description = t('error.noData');
      }

      return (
        <Empty
          className="pro-table-for-mobile__empty"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span>{description}</span>}
        />
      );
    }

    return tableValues.filter(isMobileRecord).map((value, valueIndex) => {
      return (
        <Descriptions
          title={valueIndex === 0 ? sorter() : <Divider />}
          bordered
          size={mobileTableSize}
          layout={layout}
          column={1}
          key={value?.id || valueIndex}
          className="pro-table-for-mobile"
        >
          {descriptionItems(value)}
        </Descriptions>
      );
    });
  };

  const showComponent = () => {
    return selectedView === 'card'
      ? (MobileComponentsProTable as CustomizeComponent)
      : undefined;
  };

  return {
    mobileOnSizeChangeProTable,
    showComponent,
    changeView,
  };
};

export default useProTableForMobile;
